import { createServerFn } from '@tanstack/react-start';
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/integrations/supabase/types';
import type { ComparisonOffer, ComparisonProduct } from './comparison';

// POST: o proxy da borda sempre repassa POST direto ao servidor, sem reescrita.
export const listComparisons = createServerFn({ method: 'POST' })
  .inputValidator((input: { q?: string; category?: string; brand?: string; page?: number; id?: string; min?: number; max?: number; categoryIds?: string[]; limit?: number; sort?: string }) => ({
    q: String(input?.q ?? '').slice(0, 80), category: String(input?.category ?? '').slice(0, 80),
    brand: String(input?.brand ?? '').slice(0, 80), page: Math.max(0, Math.min(100, Math.floor(Number(input?.page) || 0))),
    id: String(input?.id ?? '').slice(0, 40),
    min: Math.max(0, Number(input?.min) || 0), max: Math.max(0, Number(input?.max) || 0),
    categoryIds: (Array.isArray(input?.categoryIds) ? input.categoryIds : []).filter(x => /^[0-9a-f-]{36}$/i.test(String(x))).slice(0, 60),
    limit: Math.max(1, Math.min(24, Math.floor(Number(input?.limit) || 24))),
    sort: input?.sort === 'price' ? 'price' : 'stores',
  }))
  .handler(async ({ data }) => {
    const key = process.env['SUPABASE_PUBLISHABLE_KEY'];
    const url = process.env['SUPABASE_URL'];
    const none = { items: [] as ComparisonProduct[], categories: [] as string[], brands: [] as string[], history: [] as { day: string; brand_id: string; price: number }[], total: 0, hasMore: false, productSlug: null as string | null };
    if (!key || !url) return { ...none, error: 'Dados indisponíveis' };
    const db = createClient<Database>(url, key, {
      auth: { persistSession: false },
      global: { fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith('sb_') && headers.get('Authorization') === `Bearer ${key}`) headers.delete('Authorization');
        headers.set('apikey', key);
        return fetch(input, { ...init, headers });
      } },
    });
    const dbAny = db as any;
    const cleanedQuery = data.q.replace(/[%_,.()]/g, '').trim();
    const lim = data.limit;
    const productId = /^[0-9a-f-]{36}$/i.test(data.id) ? data.id : null;
    const { data: productRows, error } = await dbAny.rpc('search_comparison_products', {
      p_query: cleanedQuery,
      p_category: data.category,
      p_category_ids: data.categoryIds,
      p_min: data.min,
      p_max: data.max,
      p_page: data.page,
      p_limit: lim,
      p_id: productId,
      p_sort: data.sort,
    });
    const total = Number(productRows?.[0]?.total_count ?? 0);
    const products = (productRows ?? []).map(({ total_count: _totalCount, ...product }: any) => product);
    const hasMore = (total ?? 0) > (data.page + 1) * lim;
    if (error) return { ...none, error: 'Não foi possível carregar os preços.' };
    if (!products?.length) return { ...none, error: null };
    const { data: offers, error: offersError } = await dbAny.from('comparison_offers')
      .select('id,product_id,brand_id,price,shipping,checked_at,image_url,deeplink,brand:brands!inner(name,slug,logo_url,commission_type,donation_percentage,affiliate_network,is_active,network_paused,deeplink_inactive,merged_into_id,affiliate_link)')
      .in('product_id', products.map((p: any) => p.id)).eq('available', true).gt('expires_at', new Date().toISOString())
      .eq('brand.is_active', true).eq('brand.network_paused', false).eq('brand.deeplink_inactive', false)
      .is('brand.merged_into_id', null).eq('brand.affiliate_network', 'awin').not('brand.affiliate_link', 'is', null)
      .order('price', { ascending: true }).limit(500);
    if (offersError) return { ...none, error: 'Não foi possível carregar as ofertas.' };
    const byProduct = new Map<string, ComparisonOffer[]>();
    for (const row of offers ?? []) {
      const { brand, ...rest } = row as any;
      if (!brand || !/^https:\/\//i.test(rest.deeplink)) continue;
      const group = byProduct.get(rest.product_id) ?? [];
      group.push({ ...rest, brand: { name: brand.name, slug: brand.slug, logo_url: brand.logo_url, commission_type: brand.commission_type, donation_percentage: brand.donation_percentage, affiliate_network: brand.affiliate_network } });
      byProduct.set(rest.product_id, group);
    }
    const items: ComparisonProduct[] = products.map((p: any) => ({ ...p, offers: byProduct.get(p.id) ?? [] }))
      .filter((p: ComparisonProduct) => new Set(p.offers.map(o => o.brand_id)).size >= 2 && (!data.brand || p.offers.some(o => o.brand.slug === data.brand)));
    // Selo de queda: menor preço atual pelo menos 5% abaixo da média diária de 30 dias (mín. 7 dias de dados).
    if (!data.id && items.length) {
      const since30 = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
      const { data: h30 } = await dbAny.from('comparison_price_history').select('product_id,day,price').in('product_id', items.map(p => p.id)).gte('day', since30).limit(5000);
      const daily = new Map<string, Map<string, number>>();
      for (const r of h30 ?? []) { const m = daily.get(r.product_id) ?? new Map(); m.set(r.day, Math.min(m.get(r.day) ?? Infinity, Number(r.price))); daily.set(r.product_id, m); }
      for (const p of items) {
        const m = daily.get(p.id); if (!m || m.size < 7) continue;
        const avg = [...m.values()].reduce((a, b) => a + b, 0) / m.size;
        const now = Math.min(...p.offers.map(o => o.price));
        if (now < avg * 0.95) p.drop = Math.round((1 - now / avg) * 100);
      }
    }
    let history: { day: string; brand_id: string; price: number }[] = [];
    if (data.id && items[0]) {
      const since = new Date(Date.now() - 180 * 86400_000).toISOString().slice(0, 10);
      const { data: h } = await dbAny.from('comparison_price_history').select('day,brand_id,price').eq('product_id', items[0].id).gte('day', since).order('day').limit(2000);
      history = (h ?? []).map((r: any) => ({ day: r.day, brand_id: r.brand_id, price: Number(r.price) }));
    }
    return { history, items, total: total ?? items.length, hasMore, productSlug: (products[0]?.slug ?? null) as string | null, categories: [...new Set(items.map(p => p.category_name).filter((s): s is string => !!s))].sort(),
      brands: [...new Set(items.flatMap(p => p.offers.map(o => o.brand.slug)))].sort(), error: null };
  });
export const createPriceAlert = createServerFn({ method: 'POST' })
  .inputValidator((input: { productId?: string; dealId?: string; email: string; current: number; pushToken?: string | null; website?: string }) => {
    const email = String(input?.email ?? '').trim().toLowerCase().slice(0, 200);
    const current = Math.round(Number(input?.current) * 100) / 100;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) throw new Error('E-mail inválido');
    if (!(current > 0 && current < 1_000_000)) throw new Error('Preço inválido');
    const uuid = /^[0-9a-f-]{36}$/i;
    const productId = input?.productId && uuid.test(input.productId) ? input.productId : null;
    const dealId = !productId && input?.dealId && uuid.test(input.dealId) ? input.dealId : null;
    if (!productId && !dealId) throw new Error('Produto inválido');
    const pushToken = typeof input?.pushToken === 'string' && input.pushToken.length >= 20 ? input.pushToken.slice(0, 4096) : null;
    return { productId, dealId, email, current, pushToken, website: String(input?.website ?? '') };
  })
  .handler(async ({ data }) => {
    if (data.website) return { ok: true }; // honeypot
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server');
    const admin = supabaseAdmin as any;
    const { count } = await admin.from('comparison_price_alerts').select('id', { count: 'exact', head: true }).eq('email', data.email).is('notified_at', null);
    if ((count ?? 0) >= 20) return { ok: false, error: 'Você já acompanha 20 produtos. Aguarde algum aviso antes de criar outro.' };
    const { data: p } = data.productId
      ? await admin.from('comparison_products').select('id,min_price').eq('id', data.productId).eq('review_status', 'approved').maybeSingle()
      : await admin.from('deal_products').select('id,price').eq('id', data.dealId).maybeSingle();
    if (!p) return { ok: false, error: 'Produto indisponível.' };
    const referencePrice = Number(data.productId ? p.min_price : p.price);
    if (!(referencePrice > 0)) return { ok: false, error: 'Preço indisponível para monitoramento.' };
    let pushSubscriptionId: string | null = null;
    if (data.pushToken) {
      const { data: sub } = await admin.from('push_subscriptions').select('id').eq('token', data.pushToken).is('revoked_at', null).maybeSingle();
      pushSubscriptionId = sub?.id ?? null;
    }
    const row = data.productId ? { product_id: data.productId } : { deal_id: data.dealId };
    const { error } = await admin.from('comparison_price_alerts').upsert({ ...row, email: data.email, target_price: referencePrice, push_subscription_id: pushSubscriptionId, notified_at: null }, { onConflict: data.productId ? 'product_id,email' : 'deal_id,email' });
    return error ? { ok: false, error: 'Não foi possível salvar o aviso.' } : { ok: true };
  });
